import { Drawer, DrawerProps } from '@mui/material';
import { ReactNode } from 'react';
import BackdropCenteredProgress from '../../progress/backdrop-centered-progress/backdrop-centered-progress.component';
import UtilityDrawerTitle from '../utility-drawer/utility-drawer-title/utility-drawer-title.component';
import { useDrawer } from './inc-drawer.hooks';

export type IncDrawerProps<T> = {
  children: ReactNode;
  title?: ReactNode;
  DrawerProps?: DrawerProps;
  subtitle?: ReactNode;
  /**
   * Replaces the standard title block. For a drawer whose header carries more than a
   * name — a mark for its subject, a line of context above it. Left out, every drawer
   * gets `UtilityDrawerTitle` exactly as before.
   */
  header?: ReactNode;
  disableBackdropClose?: boolean;
  isLoading?: boolean;
  width?: any;
  'data-testid'?: string;
} & ReturnType<typeof useDrawer<T>>;

export type IncDrawerType<T> = Omit<IncDrawerProps<T>, 'children'>;

const IncDrawer = <T,>({
  children,
  title,
  DrawerProps = {},
  isOpen,
  close,
  subtitle,
  header,
  disableBackdropClose,
  isLoading,
  width,
  'data-testid': dataTestId,
}: IncDrawerProps<T>) => {
  const handleClose = (_e, reason) => {
    if (reason === 'backdropClick' && disableBackdropClose && !isLoading)
      return;
    close();
  };

  return (
    <Drawer
      {...DrawerProps}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      // @ts-ignore
      PaperProps={{ 'data-testid': dataTestId }}
      sx={{
        '& .MuiDrawer-paper': {
          width: width ?? ['100%', 450],
          overflowX: 'hidden',
        },
      }}
    >
      <BackdropCenteredProgress isLoading={isLoading} />
      {header ?? (
        <UtilityDrawerTitle title={title} subtitle={subtitle} onClose={close} />
      )}
      {children}
    </Drawer>
  );
};
export default IncDrawer;
